/**
 * Helper to run an async operation protected by a Web Lock across browser tabs.
 * Uses Web Locks API (`navigator.locks.request`).
 * Queues requests so concurrent operations execute safely in sequence without colliding.
 */
export async function withLock<T>(
  lockName: string,
  task: () => Promise<T>
): Promise<{ acquired: boolean; result?: T }> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks?.request) {
    try {
      return await navigator.locks.request(lockName, async (lock) => {
        if (!lock) {
          console.log(`[Lock] Lock "${lockName}" unavailable. Skipping.`);
          return { acquired: false };
        }
        const result = await task();
        return { acquired: true, result };
      });
    } catch (err) {
      console.warn(`[Lock] Failed to request lock "${lockName}":`, err);
      // Fallback to executing task directly if lock request fails unexpectedly
      const result = await task();
      return { acquired: true, result };
    }
  }

  // Fallback for environments without Web Locks API
  const result = await task();
  return { acquired: true, result };
}
